// src/pages/resumo/Resumo.js
import React, { useEffect, useState } from 'react';
import './resumo.css';

const API_BASE = process.env.REACT_APP_API_BASE;

const PERIODOS_BASE = [
  { label: '7 dias', value: 7 },
  { label: '14 dias', value: 14 },
  { label: '30 dias', value: 30 }
];

const Resumo = () => {
  const [periodo, setPeriodo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [resumoTexto, setResumoTexto] = useState('');
  const [dados, setDados] = useState(null);

  const [dots, setDots] = useState('');

  // 🔹 dados de acesso
  const [statusPlano, setStatusPlano] = useState(null);
  const [dataCadastro, setDataCadastro] = useState(null);
  const [periodosDisponiveis, setPeriodosDisponiveis] = useState([]);

  // ======================================================
  // ANIMAÇÃO DAS RETICÊNCIAS
  // ======================================================
  useEffect(() => {
    if (!loading) {
      setDots('');
      return;
    }

    const interval = setInterval(() => {
      setDots(prev => (prev.length >= 3 ? '' : prev + '.'));
    }, 450);

    return () => clearInterval(interval);
  }, [loading]);

  // ======================================================
  // BUSCAR DADOS DO USUÁRIO
  // ======================================================
  const carregarDadosUsuario = async () => {
    try {
      const res = await fetch(`${API_BASE}/painel-dados.php`, {
        credentials: 'include'
      });

      const json = await res.json();

      if (json.status !== 'sucesso') {
        throw new Error('Erro ao carregar dados do usuário.');
      }

      setStatusPlano(json.status_plano || null);
      setDataCadastro(json.data_cadastro || null);

      if (json.data_cadastro) {
        const hoje = new Date();
        const cadastro = new Date(json.data_cadastro);
        const diffMs = hoje - cadastro;
        const diasUso = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;

        const disponiveis = PERIODOS_BASE.filter(p => diasUso >= p.value);
        setPeriodosDisponiveis(disponiveis);

        if (disponiveis.length > 0) {
          setPeriodo(disponiveis[0].value);
        }
      }

    } catch (err) {
      console.error(err);
      setErro('Erro ao verificar acesso ao resumo.');
    }
  };

  // ======================================================
  // BUSCAR RESUMO
  // ======================================================
  const carregarResumo = async (dias) => {
    if (!dias) return;

    setLoading(true);
    setErro(null);

    try {
      const res = await fetch(`${API_BASE}/getresumo.php`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ periodo: dias })
      });

      if (!res.ok) {
        throw new Error('Erro ao buscar resumo.');
      }

      const json = await res.json();

      if (json.status !== 'sucesso') {
        throw new Error(json.mensagem || 'Erro desconhecido.');
      }

      setTimeout(() => {
        setResumoTexto(json.resumo_texto || '');
        setDados(json.dados || null);
        setLoading(false);
      }, 400);

    } catch (err) {
      console.error('Erro resumo:', err);
      setErro(err.message);
      setLoading(false);
    }
  };

  // ======================================================
  // INICIALIZAÇÃO
  // ======================================================
  useEffect(() => {
    carregarDadosUsuario();
  }, []);

  useEffect(() => {
    if (statusPlano === 'ativo' && periodosDisponiveis.length > 0) {
      carregarResumo(periodo);
    }
  }, [periodo, statusPlano, periodosDisponiveis]);

  // ======================================================
  // BLOQUEIO — ACESSO NÃO ATIVO
  // ======================================================
  if (statusPlano && statusPlano !== 'ativo') {
    return (
      <div className="resumo-container">
        <div className="resumo-topo">
          <img src="/assets/logo2.png" alt="Logo" className="resumo-logo" />
        </div>

        <div className="resumo-card aviso">
          <p className="resumo-texto">
            <strong>O resumo inteligente está disponível apenas no acesso completo.</strong><br />
            Libere mais dias de uso para visualizar seus insights automáticos.
          </p>

          <a href="/acesso" className="resumo-cta-btn">
            Libere mais dias de uso
          </a>
        </div>
      </div>
    );
  }

  // ======================================================
  // PLANO ATIVO, MAS SEM DADOS SUFICIENTES
  // ======================================================
  if (statusPlano === 'ativo' && periodosDisponiveis.length === 0) {
    return (
      <div className="resumo-container">
        <div className="resumo-topo">
          <img src="/assets/logo2.png" alt="Logo" className="resumo-logo" />
        </div>

        <div className="resumo-card aviso">
          <p className="resumo-texto">
            <strong>Estamos reunindo informações suficientes para gerar seu resumo.</strong><br />
            Continue registrando suas refeições e água diariamente.
          </p>
        </div>
      </div>
    );
  }

  // ======================================================
  // RENDER NORMAL
  // ======================================================
  return (
    <div className="resumo-container">

      <div className="resumo-topo">
        <img src="/assets/logo2.png" alt="Logo" className="resumo-logo" />
      </div>

      <header className="resumo-header">
        <h1>Resumo do seu período</h1>
        <p>Insights automáticos baseados nos seus registros</p>
      </header>

      <div className="resumo-filtros">
        {periodosDisponiveis.map(p => (
          <button
            key={p.value}
            className={`resumo-filtro-btn ${periodo === p.value ? 'active' : ''}`}
            onClick={() => setPeriodo(p.value)}
            disabled={loading}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="resumo-loading">
          <span className="resumo-loading-text">
            Analisando seus registros{dots}
          </span>
        </div>
      )}

      {erro && !loading && (
        <div className="resumo-erro">
          {erro}
        </div>
      )}

      {!loading && !erro && (
        <>
          <div className="resumo-card">
            <p className="resumo-texto">
              {resumoTexto || 'Nenhum resumo disponível.'}
            </p>
          </div>

          {dados && (
            <div className="resumo-metricas">
              <div className="resumo-metrica">
                <span>Dias sem registro</span>
                <strong>{dados.dias_sem_registro}</strong>
              </div>

              <div className="resumo-metrica">
                <span>Média calórica</span>
                <strong>{dados.media_calorias} kcal</strong>
              </div>

              <div className="resumo-metrica">
                <span>Média de água</span>
                <strong>{dados.media_agua_ml} ml</strong>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Resumo;
